import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import BirthdayPicker from '~/shared/components/BirthdayPicker/BirthdayPicker';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import { TextField, mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

const BirthdayContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex: 1;
  text-align: left;
  height: 72px;
  border-bottom: ${({ isSkipForm }) =>
    !isSkipForm
      ? css`1px solid ${({ theme }) => theme.colors.borderLight}`
      : 'none'};
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-radius: ${({ theme }) => theme.borderRadiusInput};
  margin: 0;

  ${mobile`
      margin-bottom: 0;
    `}

  span {
    ${({ theme: { colors, typography } }) => `
    color: ${colors.birthdaySpan};
      ${typography.values.heading8};
      margin-left: 8px;
      margin-top: 8px;
      margin-bottom: auto;
      font-weight: unset;
    `}

    ${mobile`
      margin-left: 21px;
    `}
  }
`;

const StyledBirthdayPicker = styled(BirthdayPicker)`
  margin-top: 4px;

  select {
    ${mobile`
      font-size: 16px;
    `}
  }
`;

const StyledTextField = styled(TextField)`
  ${({ theme }) => theme.quiz.zipCodeInput};
  margin-bottom: ${({ isSkipForm }) => isSkipForm && '0px'};

  ${mobile`
    margin-bottom: 0;
    color: ${({ theme }) => theme.colors.zipCodeInputColor};
    overflow: hidden;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    fieldset {
      border-color: transparent;
      margin: 0;
      padding: ${({ theme: { sizes } }) => sizes.xxs}px 14px;
      border-radius: inherit;
      &:focus-within {
        padding: ${({ theme: { sizes } }) => sizes.xxs}px 14px;
        label {
          color: ${({ theme }) => theme.colors.textFieldFieldsetLabel};
        }
      }
    }
  `}

  &.has_error {
    border-color: ${({ theme }) => theme.colors.error} !important;

    label {
      color: ${({ theme }) => theme.colors.error};
    }
    > fieldset {
      border: none;
    }
  }
`;

const ZipCodeOrBirthday = ({
  errors,
  setFieldValue,
  handleChange,
  handleBlur,
  isSkipForm,
  setErrors,
  showZip,
  handleFormUpdate,
  values,
  wrapperStyles,
  additionalStyles,
  showBirthdayInput,
}) => {
  const { tld } = useDomain();
  const isNumeric = getBrandValue({
    savage: tld !== '.com' && tld !== '.co.uk',
    default: tld !== '.ca' && tld !== '.co.uk',
  });

  const additionalBirthdayProps = useMemo(() => {
    let properties = {};
    if (showBirthdayInput) {
      properties = {
        showBirthYear: true,
        showBirthDay: false,
      };
    }

    return properties;
  }, [showBirthdayInput]);

  return (
    <>
      {showZip && (
        <StyledTextField
          name="zipCode"
          type="text"
          pattern={isNumeric ? '[0-9]*' : undefined}
          autoComplete="off"
          onChange={(e) => {
            handleChange(e);
            setErrors({ ...errors, zipCode: '' });
            handleFormUpdate({
              ...values,
              zipCode: e.target.value,
            });
          }}
          onBlur={handleBlur}
          value={values.zipCode}
          wrapperStyle={wrapperStyles}
          style={additionalStyles}
          label={
            <FormattedMessage
              id="quiz.sizing_screen_zip_code_header"
              defaultMessage="Zip Code *"
            />
          }
          {...(errors.zipCode ? { className: 'has_error' } : {})}
          data-autotag="quiz-postal-code"
          data-pii="pii-postal-code"
          $isSkipForm={isSkipForm}
        />
      )}
      {(!showZip || showBirthdayInput) && (
        <BirthdayContainer isSkipForm={isSkipForm}>
          {showBirthdayInput ? (
            <FormattedMessage
              id="quiz.sizing_screen_birthday_optional"
              defaultMessage="Birthday"
            />
          ) : (
            <FormattedMessage
              id="quiz.sizing_screen_birthday"
              defaultMessage="Birthday"
            />
          )}
          <StyledBirthdayPicker
            birthDay={values.birthDay}
            birthMonth={values.birthMonth}
            birthYear={values.birthYear}
            handleChange={(e) => {
              handleChange(e);
              handleFormUpdate({
                ...values,
                [e.target.name]: e.target.value,
              });
            }}
            showBirthYear={false}
            errors={errors}
            data-autotag="quiz-birthday"
            data-pii="pii-birthday"
            {...additionalBirthdayProps}
          />
        </BirthdayContainer>
      )}
    </>
  );
};

ZipCodeOrBirthday.propTypes = {
  additionalStyles: PropTypes.any,
  errors: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleFormUpdate: PropTypes.func.isRequired,
  isSkipForm: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  showBirthdayInput: PropTypes.bool,
  showZip: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired,
  wrapperStyles: PropTypes.any,
};

export default ZipCodeOrBirthday;
